import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FolderOpen, Search, Calendar, Image, ChevronDown, ChevronUp, RefreshCw, X, Building2 } from 'lucide-react';
import { useBusiness, type BusinessView } from '@/contexts/BusinessContext';
import { usePageTour, WelcomeModal } from '@/walkthrough';
import { evidenceLibraryPageTour } from '@/walkthrough/tourSteps';
import { useVisitsWithEvidence } from '@/hooks/useVisits';
import type { VisitWithEvidence } from '@/services/visits';

// ===========================================
// Types
// ===========================================

interface VisitCardProps {
  visit: VisitWithEvidence;
  onPhotoClick: (photoUrl: string) => void;
}

// ===========================================
// Visit Card Component
// ===========================================

function VisitCard({ visit, onPhotoClick }: VisitCardProps) {
  const [expanded, setExpanded] = useState(false);
  const { getBusinessLabel } = useBusiness();

  const visitDate = visit.visitDate ? new Date(visit.visitDate) : null;
  const formattedDate = visitDate
    ? visitDate.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : 'Unknown date';

  // Determine if notes should be truncated
  const notes = visit.notes || '';
  const shouldTruncate = notes.length > 150;
  const displayNotes = shouldTruncate && !expanded ? notes.substring(0, 150) + '...' : notes;

  return (
    <div className="card hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Company name */}
          <Link
            to={`/companies/${visit.companyId}`}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
          >
            <Building2 size={16} />
            {visit.companyName}
          </Link>

          {/* Visit date and visitor */}
          <div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
            <Calendar size={14} />
            <span>{formattedDate}</span>
            <span className="text-gray-400">|</span>
            <span>by {visit.visitedByName}</span>
          </div>

          {/* Business badge */}
          <div className="mt-2">
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                visit.business === 'azdisaster'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-slate-100 text-slate-700'
              }`}
            >
              {getBusinessLabel(visit.business as 'azdisaster' | 'blacksmith')}
            </span>
          </div>

          {/* Notes */}
          {notes && (
            <div className="mt-3">
              <p className="text-sm text-gray-700">{displayNotes}</p>
              {shouldTruncate && (
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 mt-1"
                >
                  {expanded ? (
                    <>
                      <ChevronUp size={14} />
                      Show less
                    </>
                  ) : (
                    <>
                      <ChevronDown size={14} />
                      See more
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Photo thumbnails */}
      {visit.evidencePhotos && visit.evidencePhotos.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
          {visit.evidencePhotos.map((photo) => (
            <button
              key={photo.id}
              onClick={() => onPhotoClick(photo.downloadURL)}
              className="relative group cursor-pointer"
            >
              <img
                src={photo.downloadURL}
                alt={photo.fileName}
                className="w-16 h-16 object-cover rounded-lg border border-gray-200"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 rounded-lg flex items-center justify-center transition-opacity">
                <Image size={20} className="text-white" />
              </div>
            </button>
          ))}
          <span className="self-center text-xs text-gray-500 ml-2">
            {visit.evidencePhotos.length} photo{visit.evidencePhotos.length !== 1 ? 's' : ''}
          </span>
        </div>
      )}
    </div>
  );
}

// ===========================================
// Photo Modal Component
// ===========================================

function PhotoModal({ photoUrl, onClose }: { photoUrl: string; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
      >
        <X size={24} />
      </button>
      <img
        src={photoUrl}
        alt="Evidence photo"
        className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

// ===========================================
// Main Component
// ===========================================

export function EvidenceLibraryPage() {
  const { currentBusinessView, setCurrentBusinessView, getBusinessViewLabel } = useBusiness();
  const { showWelcomeModal, startTour, skipTour, globalOptOut } = usePageTour(evidenceLibraryPageTour);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);

  // Use debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data: visits, isLoading, error, refetch } = useVisitsWithEvidence(debouncedSearch);

  // Filter visits by business view
  const filteredVisits = useMemo(() => {
    if (!visits) return [];
    if (currentBusinessView === 'both') return visits;
    return visits.filter((v) => v.business === currentBusinessView);
  }, [visits, currentBusinessView]);

  const businessOptions: BusinessView[] = ['both', 'azdisaster', 'blacksmith'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Evidence Library</h1>
          <p className="page-description">
            All visits with evidence photos, ordered by visit date.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="btn btn-secondary self-start sm:self-auto"
          disabled={isLoading}
        >
          <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Search and filters */}
      <div data-tour="evidence-search" className="card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by company name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input pl-10"
            />
          </div>
          <div>
            <select
              value={currentBusinessView}
              onChange={(e) => setCurrentBusinessView(e.target.value as BusinessView)}
              className="input"
            >
              {businessOptions.map((option) => (
                <option key={option} value={option}>
                  {getBusinessViewLabel(option)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="card">
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="animate-spin text-blue-600" size={32} />
            <span className="ml-3 text-gray-600">Loading evidence...</span>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="card bg-red-50 border-red-200">
          <p className="text-red-700">Failed to load evidence: {(error as Error).message}</p>
          <button
            onClick={() => refetch()}
            className="btn btn-secondary mt-3"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Evidence list */}
      {!isLoading && !error && (
        <>
          {/* Results count */}
          <div className="text-sm text-gray-600">
            Showing {filteredVisits.length} visit{filteredVisits.length !== 1 ? 's' : ''} with evidence
          </div>

          {filteredVisits.length > 0 ? (
            <div data-tour="evidence-cards" className="space-y-4">
              {filteredVisits.map((visit) => (
                <VisitCard
                  key={visit.id}
                  visit={visit}
                  onPhotoClick={setSelectedPhoto}
                />
              ))}
            </div>
          ) : (
            <div className="card">
              <div className="empty-state">
                <FolderOpen size={48} className="empty-state-icon" />
                <p className="text-lg font-medium text-gray-900">No evidence found</p>
                <p className="mt-1">
                  {searchQuery
                    ? 'No visits match your search criteria.'
                    : 'Visits with evidence photos will appear here when uploaded by reps.'}
                </p>
              </div>
            </div>
          )}
        </>
      )}

      {/* Info card */}
      <div className="card bg-blue-50 border-blue-200">
        <h3 className="font-medium text-blue-900 mb-2">About Evidence Library</h3>
        <div className="text-sm text-blue-800 space-y-2">
          <p>
            This page shows all company visits that have evidence photos attached.
            Evidence is typically uploaded by sales reps when providing compensation
            to referral partners (gift cards, cash, etc.).
          </p>
          <p>
            Click on a company name to view the full company details and visit history.
            Click on photo thumbnails to view them in full size.
          </p>
        </div>
      </div>

      {/* Photo modal */}
      {selectedPhoto && (
        <PhotoModal photoUrl={selectedPhoto} onClose={() => setSelectedPhoto(null)} />
      )}

      <WelcomeModal
        pageName={evidenceLibraryPageTour.pageName}
        onStartTour={startTour}
        onSkip={skipTour}
        onGlobalOptOut={globalOptOut}
        open={showWelcomeModal}
      />
    </div>
  );
}
